// src/screens/TeamStreamScreen.js
// ============================================================================
// SQUAD STREAM WATCH PAGE — Team Streams v1 (2026-07-21)
//
// Pooled squad streaming: the whole squad on one page, one shared chat.
// Browser-size aware, modeled on StreamWatchScreen (whose player/overlay/chat
// components are imported, not forked).
//
// Mosaic (evergreen 4.1 / plan 6K step 1, founder 2026-09-22): the VIEWER
// picks which live members are on screen; everyone else sits in a poster
// rail (avatar, live badge, viewer count) and taps in to swap.
//  - Wide (>=1000px): 1 to 4 live tiles in an equal grid (2x2 at four).
//    Opens at two; "Show up to 4" is an opt-in. Squad chat is a full-height
//    right rail on the squad's own streamChat session.
//  - Narrow: 1 or 2 live tiles stacked top and bottom, never side by side.
//    Opens at one; tapping a poster adds the second.
//  - Exactly one tile carries audio (gold ring). Tap a tile to move audio to
//    it, double-tap to make it full screen.
//  - The selection persists per squad in localStorage (web only), so a
//    regular viewer's layout comes back.
// Every live tile is a full source-bitrate HLS pull (the relay does not
// transcode yet), so the caps above are an egress decision (6K step 2 adds
// the low-bitrate tile rendition). Non-selected members are posters, which
// cuts the old always-every-member-live page down, not up.
//
// "Focused" member = the tile carrying audio. It is also the streamAudience
// heartbeat target, so the existing per-streamer watch metrics stay honest
// (no new roster collection).
//
// Rumbles (evergreen 3.3) go to the squad, not the focused member: one
// purchase, split by the server across the live, linked members who can
// receive it. The page shows the chat's Rumble button only on getTeamStream's
// rumblesEnabled and never to squad members (the server refuses them too);
// StreamRumble, mounted once at screen level, owns the meter, picker and
// burst.
//
// Super Crits sent here celebrate the squad: one purchase, split equally
// across the SPLIT SET = members live AND linked AND verified (evergreen 3.2:
// verification is the only earn gate; getTeamStream's per-member tipEligible
// carries it, and a missing flag counts as eligible so an older server
// behaves exactly as before). computeSquadSplit (shared constants mirror of
// functions/constants/teamStreams.js) runs over that same set; the preview
// says only how many members share it (fan copy stays money-free), with the
// server's getTeamSuperCritSetup recipientCount preferred when present. Both
// purchase calls send previewV: 2 so the server knows this preview already
// left unverified members out (an older tab is asked to refresh instead).
//
// Names/avatars come ONLY from the getTeamStream response (server-sanitized
// via public_profiles). This screen never reads users/{uid} for display.
//
// Team memberships (evergreen 3.5b, design 13.8): "Join Team X" / "Member" in
// the info block, keyed on the viewer's own team pair listener
// (useMyMembership('team', teamStreamId); teamStreamId === teamId by
// construction), never on the 45 s poll, so a join shows at once and a
// failed poll never tears down a payment form. Join follows getTeamStream's
// membershipsEnabled hint and hides for anyone on the FULL roster
// (viewerOnRoster, never the slice of 8). The squad chat runs as the team's
// room: gateKind 'team', the team badge target, the roster admitted by the
// rule. Fan copy stays money-free; "Join", never "Subscribe".
// ============================================================================

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Image,
  Platform,
  Modal,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  doc, getDoc, setDoc, updateDoc, increment, serverTimestamp, onSnapshot,
} from 'firebase/firestore';
import { db, auth } from '../../firebaseConfig';
import { Ionicons } from '@expo/vector-icons';
import Header from '../components/Header';
import { useError } from '../components/ErrorContext';
import { LivePlayerOverlay, StreamChat } from './StreamWatchScreen';
import LiveHlsPlayer from '../components/LiveHlsPlayer';
import { computeSquadSplit } from '../constants/teamStreams';
import { streamGameName } from '../constants/streamGames';
import StreamRumble from '../components/StreamRumble';
import JoinMembershipModal from '../components/JoinMembershipModal';
import MemberPanel, { restoreFocus } from '../components/MemberPanel';
import useMembershipsLive from '../hooks/useMembershipsLive';
import {
  useMyMembership,
  setTeamChatMembersOnly,
  teamDisplayName,
  teamMemberTenureLabel,
} from '../utils/membershipPayment';
import SpendingLimitNotice from '../components/SpendingLimitNotice';
import useTipLimitNotices from '../hooks/useTipLimitNotices';
import {
  limitLines, cardLine, fitsLimit, savedCardStatus, cardStatusFromError, limitFromError,
  CONFIRMING_TEXT, STILL_CONFIRMING_TEXT,
} from '../utils/spendingLimit';

const COLORS = {
  BG: '#0b0b0b',
  CARD: '#111',
  CARD_BORDER: '#222',
  GOLD: '#FCD000',
  TEXT: '#EDEDED',
  DIM: '#888',
  LIVE: '#e02222',
};

const CHAT_WIDTH = 340;

// Mosaic caps (plan 6K step 1). Wide opens at two live tiles and can opt in
// to four; narrow is one tile with room for a second. These are egress
// decisions on the single source rendition, not taste.
const MOSAIC_MAX_WIDE = 4;
const MOSAIC_DEFAULT_WIDE = 2;
const MOSAIC_MAX_NARROW = 2;
const MOSAIC_DEFAULT_NARROW = 1;
const MOSAIC_GAP = 12;
const DOUBLE_TAP_MS = 320;
const LAYOUT_STORAGE_PREFIX = 'critcash.squadTiles.';

// Per-squad layout memory (web only, best-effort: private mode / SSR / a
// full quota all fail quietly, same idiom as CartContext).
const readSavedLayout = (teamId) => {
  if (Platform.OS !== 'web' || typeof window === 'undefined' || !teamId) return null;
  try {
    const store = window.localStorage; // the getter itself throws with site data blocked
    if (!store) return null;
    const raw = store.getItem(LAYOUT_STORAGE_PREFIX + teamId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== 1 || !Array.isArray(parsed.uids)) return null;
    return {
      uids: parsed.uids.filter((u) => typeof u === 'string'),
      audioUid: typeof parsed.audioUid === 'string' ? parsed.audioUid : null,
      capWide: Number(parsed.capWide) || 0,
      trimmed: parsed.trimmed === true,
    };
  } catch (_) { return null; }
};
const writeSavedLayout = (teamId, layout) => {
  if (Platform.OS !== 'web' || typeof window === 'undefined' || !teamId) return;
  try {
    const store = window.localStorage;
    if (!store) return;
    store.setItem(LAYOUT_STORAGE_PREFIX + teamId, JSON.stringify({ v: 1, ...layout }));
  } catch (_) { /* quota / private mode / blocked: the layout just does not persist */ }
};
const WIDE_BREAKPOINT = 1000;

// Amber/yellow Super Crit tiers need dark text; the rest carry white.
const DARK_TEXT_TIER_COLORS = new Set(['#FFB300']);
const superCritTextColor = (bg) => (DARK_TEXT_TIER_COLORS.has(String(bg || '').toUpperCase()) ? '#0b0b0b' : '#fff');
const fmtDollars = (centsAmt) => {
  const d = (Number(centsAmt) || 0) / 100;
  return `$${d % 1 ? d.toFixed(2) : d.toFixed(0)}`;
};
const fmtCount = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n || 0));

// Display name for a member's game — 'other' shows the streamer's own text.
const memberGameLabel = (m) => (m?.game === 'other' && m?.gameCustom ? m.gameCustom : (m?.game ? streamGameName(m.game) : null));

// Same fixed ladder as the personal Super Crit modal (server tiers win when
// the setup call returns them).
const FALLBACK_TIERS = [
  { id: 'sc_100', cents: 100, color: '#1565C0' },
  { id: 'sc_300', cents: 300, color: '#00B8D4' },
  { id: 'sc_500', cents: 500, color: '#00BFA5' },
  { id: 'sc_1000', cents: 1000, color: '#FFB300' },
  { id: 'sc_2500', cents: 2500, color: '#F57C00' },
  { id: 'sc_5000', cents: 5000, color: '#E91E63' },
  { id: 'sc_10000', cents: 10000, color: '#E62117' },
  { id: 'sc_50000', cents: 50000, color: '#D00000' },
];

// The getTeamStream response is server-sanitized (public_profiles names,
// opaque avatar URLs). Normalize member field names defensively so a rename
// on the callable side degrades gracefully instead of blanking the page.
// tipEligible (3.2) = the member passes the earn gate (server-computed from
// their users doc). Only an explicit false leaves them out of the split; a
// server that predates the flag omits it, and everyone stays eligible.
const normalizeMember = (m) => ({
  uid: m.uid || m.userId,
  userName: m.userName || m.displayName || 'Player',
  avatarUrl: m.avatarUrl || m.avatar || null,
  live: !!m.live,
  linked: !!m.linked,
  playbackUrl: m.playbackUrl || null,
  title: m.title || null,
  game: m.game || null,
  gameCustom: m.gameCustom || null,
  viewers: Math.max(0, Number(m.viewers) || 0),
  tipEligible: m.tipEligible !== false,
});

// Fan-facing refusal line when members are live and linked but none of them
// can receive Super Crits yet (3.2; same text as the server's team refusal).
const SQUAD_TIPS_NOT_OPEN = "Squad Super Crits aren't open for the members streaming right now.";

const initials = (name) => (name || '??').slice(0, 2).toUpperCase();

const sameList = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

// ---------------------------------------------------------------------------
// MOSAIC TILE - one live member on screen. Owns its container ref and the
// active <video> element so the shared LivePlayerOverlay (play / mute / LIVE
// / fullscreen) works per tile. Audio contract: the tile carrying audio gets
// muted={!audioOn}; every other tile is hard-muted by prop, so two feeds can
// never be audible at once (the same rule the old focused/grid split kept).
// Tap = move audio here. Double-tap = fullscreen this tile. The overlay's
// speaker on a silent tile also moves audio here (unmute means "hear this
// one"); on the audio tile it is the plain mute toggle.
// ---------------------------------------------------------------------------
const MosaicTile = ({
  member, width, hasAudio, audioOn, canRemove,
  onSelectAudio, onToggleAudio, onRemove, onAutoplayBlocked,
}) => {
  const containerRef = useRef(null);
  const [videoEl, setVideoEl] = useState(null);
  const lastTapRef = useRef(0);
  const silent = !(hasAudio && audioOn);

  const onPress = () => {
    const now = Date.now();
    if (now - lastTapRef.current < DOUBLE_TAP_MS) {
      lastTapRef.current = 0;
      if (typeof document !== 'undefined') {
        const el = containerRef.current;
        if (!document.fullscreenElement) el?.requestFullscreen?.().catch(() => {});
        else document.exitFullscreen?.();
      }
      return;
    }
    lastTapRef.current = now;
    onSelectAudio();
  };

  return (
    <Pressable
      ref={containerRef}
      style={[styles.mosaicTile, { width }, hasAudio && styles.mosaicTileAudio]}
      onPress={onPress}
    >
      <LiveHlsPlayer
        src={member.playbackUrl}
        muted={silent}
        onActiveVideoChange={setVideoEl}
        onAutoplayBlocked={hasAudio ? onAutoplayBlocked : undefined}
      />
      <LivePlayerOverlay
        videoEl={videoEl}
        containerRef={containerRef}
        muted={silent}
        onMuteToggle={hasAudio ? onToggleAudio : onSelectAudio}
      />
      <View style={styles.focusedNameBar} pointerEvents="none">
        <View style={styles.tileLiveBadgeInline}><Text style={styles.tileLiveBadgeText}>LIVE</Text></View>
        <Text style={styles.focusedNameText} numberOfLines={1}>{member.userName}</Text>
        <Text style={styles.tileViewersText}>{`  ${fmtCount(member.viewers)}`}</Text>
        {hasAudio ? (
          <Ionicons name={audioOn ? 'volume-high' : 'volume-mute'} size={12} color={COLORS.GOLD} style={{ marginLeft: 6 }} />
        ) : null}
      </View>
      {canRemove ? (
        <TouchableOpacity style={styles.mosaicRemoveBtn} onPress={onRemove} hitSlop={8} accessibilityLabel="Remove from screen">
          <Ionicons name="close" size={14} color="#fff" />
        </TouchableOpacity>
      ) : null}
    </Pressable>
  );
};

// ---------------------------------------------------------------------------
// MAIN SCREEN
// ---------------------------------------------------------------------------
export default function TeamStreamScreen({ navigation, route }) {
  const teamStreamId = route?.params?.teamStreamId;
  const { showError } = useError();
  const isFocused = useIsFocused();
  const functions = getFunctions();
  const { width, height } = useWindowDimensions();
  const isWide = width >= WIDE_BREAKPOINT;

  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  // focusedUid = the tile carrying audio. selectedUids = the viewer's WANTED
  // tiles in order, offline members included (they come back on screen when
  // they go live again; only playable ones render, cut to the viewport cap at
  // render time, so a resize or a phone never destroys a desktop layout).
  // capWide = the wide opt-in (2 or 4); narrow is always capped at
  // MOSAIC_MAX_NARROW.
  const [focusedUid, setFocusedUid] = useState(null);
  const [selectedUids, setSelectedUids] = useState([]);
  const [capWide, setCapWide] = useState(MOSAIC_DEFAULT_WIDE);
  // ONE piece of audio state: tiles are hard-muted by prop, the audio tile's
  // muted is !audioOn, and LiveHlsPlayer applies it to whichever element is
  // active. Tap-to-hear: selecting a tile arms it; the overlay speaker toggles
  // it; a non-tap refocus (member went offline) leaves it alone, so an
  // explicit mute survives.
  const [audioOn, setAudioOn] = useState(false);
  const [myName, setMyName] = useState(null);
  // Squad Rumble (evergreen 3.3): the squad id the picker was opened for,
  // not a boolean, so a squad switch can never carry an open picker (or its
  // checkout) over to another squad. StreamRumble owns everything else.
  const [rumbleOpenFor, setRumbleOpenFor] = useState(null);
  // Squad Super Crit — one purchase, every member shares it.
  const [showSuper, setShowSuper] = useState(false);
  const [superSetup, setSuperSetup] = useState(null);
  const [superTier, setSuperTier] = useState('sc_500');
  const [superMsg, setSuperMsg] = useState('');
  const [superBusy, setSuperBusy] = useState(false);
  const [superError, setSuperError] = useState(null);
  const [superSent, setSuperSent] = useState(false);
  const [superEmbedded, setSuperEmbedded] = useState(null); // { clientSecret, publishableKey, cardCheck, purchaseId } | null
  const superCheckoutContainerRef = useRef(null);
  const superCheckoutInstanceRef = useRef(null);
  // Purchase limits ('limits' mode only; 'cap' sends no limit object and every
  // notice stays null). The room is the payer's own, the same on every squad.
  const superLimit = superSetup?.limit && typeof superSetup.limit === 'object' ? superSetup.limit : null;
  const superSavedStatus = savedCardStatus(superLimit);
  const superTips = useTipLimitNotices(superLimit?.verify);
  // Chat moderation + reporting (same pattern as StreamWatch): tap a handle
  // in chat -> user card (profile / hide / mute-by-owner / report).
  const [chatUserCard, setChatUserCard] = useState(null);
  const [hiddenUids, setHiddenUids] = useState(() => new Set());
  const [mutedUids, setMutedUids] = useState(() => new Set());
  const [modBusy, setModBusy] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState('inappropriate');
  const [reportDetails, setReportDetails] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportStatus, setReportStatus] = useState(null);
  // Team memberships (3.5b): the squad id the Join modal was opened for (a
  // squad switch can never carry an open form over), and the member panel.
  const [joinOpenFor, setJoinOpenFor] = useState(null);
  const [memberPanelOpen, setMemberPanelOpen] = useState(false);
  const teamJoinBtnRef = useRef(null);
  const memberBtnRef = useRef(null);
  const joinOpenerRef = useRef(null);

  // Layout memory: hydrated once per squad. `trimmed` flips only when the
  // viewer REMOVES a tile (or lowers the cap) and stops the default auto-fill
  // from re-adding one; a plain audio tap or an add never flips it. `dirty`
  // flips on any viewer action and gates the localStorage write, so a saved
  // layout is never overwritten by the hydrate pass itself.
  const hydratedForRef = useRef(null);
  const trimmedRef = useRef(false);
  const dirtyRef = useRef(false);
  const loadedOnce = useRef(false);
  const anonSessionId = useRef(`s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`);

  const members = team?.members || [];
  const focusedMember = members.find((m) => m.uid === focusedUid) || null;
  const focusedLive = !!(focusedMember && focusedMember.live && focusedMember.playbackUrl);
  const liveMembers = members.filter((m) => m.live);
  const liveCount = liveMembers.length;
  // Mosaic sizing for THIS viewport. shownTiles is what actually renders:
  // the selection, live-only, cut to the cap (the effect below guarantees the
  // audio tile is inside the cut before it can matter).
  const maxTiles = isWide ? Math.min(MOSAIC_MAX_WIDE, Math.max(1, capWide)) : MOSAIC_MAX_NARROW;
  const defaultShown = isWide ? Math.min(MOSAIC_DEFAULT_WIDE, maxTiles) : MOSAIC_DEFAULT_NARROW;
  const playableUids = members.filter((m) => m.live && m.playbackUrl).map((m) => m.uid);
  const knownUid = (u) => members.some((m) => m.uid === u);
  const playable = (u) => playableUids.includes(u);
  const shownUids = selectedUids.filter(playable).slice(0, maxTiles);
  const shownTiles = shownUids.map((u) => members.find((m) => m.uid === u));
  // The Super Crit POOL (founder 2026-07-21): members who are live AND have
  // linked themselves to this squad (per-member opt-in, default off).
  const linkedLiveMembers = members.filter((m) => m.live && m.linked);
  // The SPLIT SET (3.2): the pool minus members who have not passed the earn
  // gate (tipEligible false). This is who shares a squad Super Crit, and the
  // server's recipient snapshot applies the same filter.
  const splitMembers = linkedLiveMembers.filter((m) => m.tipEligible);
  const totalViewers = members.reduce((sum, m) => sum + (m.live ? m.viewers : 0), 0);
  const myUid = auth.currentUser?.uid || null;
  const myMember = myUid ? members.find((m) => m.uid === myUid) || null : null;
  const isSquadMember = !!myMember;
  const isOwner = !!myUid && myUid === team?.ownerUid;
  // Team memberships (3.5b, 13.8). The viewer's own team pair doc, one read
  // per change (never the poll); the constant object signed out or for a
  // malformed id. The roster test reads the FULL roster (viewerOnRoster),
  // with the shown slice as the fallback for an older server.
  const tm = useMyMembership('team', teamStreamId || null);
  const { teamLive, teamEverLaunched } = useMembershipsLive();
  const onRoster = team?.viewerOnRoster === true || isSquadMember;
  // The canSquadRumble pattern: a stale tap between polls is refused on the
  // same value. Member shows even after the team stops selling.
  const showTeamJoin = team?.membershipsEnabled === true && team.forId === teamStreamId
    && !onRoster && !tm.loading && !tm.error && !tm.isMember;
  const showTeamMember = tm.isMember === true;
  // The team's name for the Join modal and the member panel, kept through a
  // failed poll (team -> null) so an open form never loses its title. Only a
  // live team's name (a squad whose team is gone reads 'Squad').
  const joinNameRef = useRef(null);
  if (team?.forId === teamStreamId && team?.ownerUid && team.name) {
    joinNameRef.current = { id: teamStreamId, name: team.name };
  }
  const joinName = joinNameRef.current?.id === teamStreamId ? joinNameRef.current.name : null;
  const teamNoun = teamDisplayName(joinName);
  // Squad master switch: OFF = dormant - no squad Super Crits.
  const linkOn = team?.linkActive !== false;
  // The lane needs the master switch ON and at least one member in the split
  // set (server-enforced too).
  const canSquadSuperCrit = linkOn && splitMembers.length > 0;
  // getTeamSuperCritSetup's squad verdict (3.2), read only from THIS squad's
  // setup. A null setup (still loading, or the call failed) or a server that
  // predates the fields is UNKNOWN: fall back to the roster, which is
  // today's behavior. recipientCount (the server's split set size) wins over
  // the roster count for the preview.
  const setupForSquad = superSetup && superSetup.forId === teamStreamId ? superSetup : null;
  const setupTipsKnown = typeof setupForSquad?.tipsEnabled === 'boolean';
  const setupRecipientCount = Number.isFinite(setupForSquad?.recipientCount) ? setupForSquad.recipientCount : null;
  // Modal state: Send is disabled and the not-open line shows when the server
  // says no, or (verdict unknown) when members are live and linked but none
  // is in the split set. Nobody linked live at all keeps today's behavior
  // (the server refuses with its own message).
  const superNotOpen = setupTipsKnown
    ? setupForSquad.tipsEnabled === false
    : (linkOn && linkedLiveMembers.length > 0 && splitMembers.length === 0);

  // ---------------------------------------------------------------------
  // Data: getTeamStream, 45s poll (same cadence as the personal watch page)
  // ---------------------------------------------------------------------
  const fetchTeam = useCallback(async () => {
    if (!teamStreamId) { setLoading(false); return; }
    try {
      const fn = httpsCallable(functions, 'getTeamStream');
      const result = await fn({ teamStreamId });
      const data = result.data || null;
      setTeam(data ? {
        ...data,
        forId: teamStreamId, // the roster the mosaic effect may reconcile under this id
        members: (Array.isArray(data.members) ? data.members : [])
          .map(normalizeMember)
          .filter((m) => !!m.uid),
      } : null);
    } catch (err) {
      const raw = String(err?.message || '');
      console.warn('[TeamStream] fetch failed:', raw);
      // Polling failures stay quiet; a hard first-load failure (that is not a
      // plain not-found) surfaces through the error modal, never window.alert.
      if (!loadedOnce.current && !raw.includes('not-found')) {
        showError('Could not load the squad', 'Please try again in a moment.');
      }
      setTeam(null);
    } finally {
      loadedOnce.current = true;
      setLoading(false);
    }
  }, [functions, teamStreamId, showError]);

  // Squad switch while mounted (deep link / notification tap to another
  // squad): drop the old roster and selection before the new one loads, so
  // nothing is reconciled or persisted under the wrong id.
  useEffect(() => {
    setTeam(null);
    setLoading(true);
    setSelectedUids([]);
    setFocusedUid(null);
    dirtyRef.current = false;
    trimmedRef.current = false;
    setJoinOpenFor(null);
    setMemberPanelOpen(false);
  }, [teamStreamId]);

  useEffect(() => {
    if (!isFocused) return;
    fetchTeam();
    const poll = setInterval(fetchTeam, 45000);
    return () => clearInterval(poll);
  }, [isFocused, fetchTeam]);

  // Mosaic reconciliation. Runs on every roster poll and viewport change:
  //  1. hydrate the saved layout once per squad (offline members included;
  //     a non-empty saved layout that was trimmed keeps its trim);
  //  2. keep the audio tile inside the visible cut by moving it to the
  //     front (never cut the wanted list itself: the viewport does that at
  //     render time);
  //  3. auto-fill with live members up to the default count until the viewer
  //     trims, and never leave the screen empty while anyone is live;
  //  4. audio follows a shown tile; with nobody live it falls to the first
  //     member (the info block still needs someone).
  // Every setState is change-guarded so the effect converges.
  useEffect(() => {
    if (!team || team.forId !== teamStreamId || !members.length) return;
    const playable = playableUids;

    if (hydratedForRef.current !== teamStreamId) {
      hydratedForRef.current = teamStreamId;
      const saved = readSavedLayout(teamStreamId);
      const knownUids = members.map((m) => m.uid);
      const uids = saved ? saved.uids.filter((u) => knownUids.includes(u)) : [];
      trimmedRef.current = !!saved && uids.length > 0 && saved.trimmed;
      if (saved && saved.capWide >= 1) setCapWide(Math.min(MOSAIC_MAX_WIDE, saved.capWide));
      if (uids.length) {
        setSelectedUids(uids);
        setFocusedUid(uids.includes(saved.audioUid) ? saved.audioUid : uids[0]);
        return; // the state writes re-run this effect for the fill pass
      }
    }

    let next = selectedUids.filter((u) => knownUid(u));
    const visible = () => next.filter((u) => playable.includes(u)).slice(0, maxTiles);
    if (focusedUid && playable.includes(focusedUid) && next.includes(focusedUid) && !visible().includes(focusedUid)) {
      next = [focusedUid, ...next.filter((u) => u !== focusedUid)];
    }
    const fillTo = trimmedRef.current ? 1 : defaultShown;
    for (const u of playable) {
      if (visible().length >= fillTo) break;
      if (!next.includes(u)) next.push(u);
    }
    if (!sameList(next, selectedUids)) setSelectedUids(next);

    const shown = visible();
    const audio = shown.includes(focusedUid) ? focusedUid : (shown[0] || (playable.length ? null : members[0].uid));
    if (audio && audio !== focusedUid) setFocusedUid(audio);
  }, [team, teamStreamId, isWide, capWide, selectedUids, focusedUid, maxTiles, defaultShown]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist only after the viewer has acted in this session (never from the
  // hydrate pass, which would overwrite a layout with a live-pruned copy).
  useEffect(() => {
    if (!dirtyRef.current || hydratedForRef.current !== teamStreamId) return;
    writeSavedLayout(teamStreamId, { uids: selectedUids, audioUid: focusedUid, capWide, trimmed: trimmedRef.current });
  }, [teamStreamId, selectedUids, focusedUid, capWide]);

  // Audio: exactly one audible player, always the focused tile, enforced by
  // props (other tiles render muted, the audio tile gets !audioOn), not by
  // sweeping video elements. The initial load is silent (autoplay policies);
  // a genuine tile tap is what first arms audio.
  const selectAudio = (uid) => {
    dirtyRef.current = true;
    setAudioOn(true); // tap-to-hear: tapping a member means "watch them, with sound"
    setFocusedUid(uid);
  };

  // Rail tap: add the member if the visible cut has room (inserted where
  // they will actually show); otherwise swap them in for the oldest visible
  // tile that is not carrying audio (the only tile, when the cap is one).
  // Either way they get the audio.
  const addOrSwapMember = (uid) => {
    if (!playableUids.includes(uid)) return;
    dirtyRef.current = true;
    setSelectedUids((prev) => {
      const cur = prev.filter((u) => knownUid(u));
      const shown = cur.filter((u) => playableUids.includes(u)).slice(0, maxTiles);
      if (shown.includes(uid)) return cur;
      const without = cur.filter((u) => u !== uid);
      if (shown.length < maxTiles) {
        // Insert right after the last visible tile so the newcomer is inside
        // the cut (an offline member parked later in the list stays parked).
        const last = shown[shown.length - 1];
        const at = last ? without.indexOf(last) + 1 : 0;
        return [...without.slice(0, at), uid, ...without.slice(at)];
      }
      const victim = shown.find((u) => u !== focusedUid) ?? shown[0];
      return without.map((u) => (u === victim ? uid : u));
    });
    setAudioOn(true);
    setFocusedUid(uid);
  };

  const removeTile = (uid) => {
    dirtyRef.current = true;
    trimmedRef.current = true;
    setSelectedUids((prev) => (prev.length > 1 ? prev.filter((u) => u !== uid) : prev));
  };

  // Wide opt-in: raising the cap fills the new room with the next live
  // members (that is what "show up to 4" means); lowering it trims the
  // wanted list to the cap, keeping the audio tile, and counts as a trim.
  const setWideCap = (cap) => {
    dirtyRef.current = true;
    if (cap < capWide) trimmedRef.current = true;
    setCapWide(cap);
    setSelectedUids((prev) => {
      let next = prev.filter((u) => knownUid(u));
      const visible = () => next.filter((u) => playableUids.includes(u)).slice(0, cap);
      if (focusedUid && playable(focusedUid) && next.includes(focusedUid) && !visible().includes(focusedUid)) {
        next = [focusedUid, ...next.filter((u) => u !== focusedUid)];
      }
      if (cap < capWide) next = next.filter((u) => visible().includes(u) || !playable(u));
      for (const u of playableUids) {
        if (visible().length >= cap) break;
        if (!next.includes(u)) next.push(u);
      }
      return next;
    });
  };

  // ---------------------------------------------------------------------
  // Watch metering: heartbeat the FOCUSED member's existing month-keyed
  // streamAudience roster (clone of the StreamWatch rosterBeat) — switches
  // targets when focus switches, keeping per-streamer metrics honest.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!isFocused || !focusedUid || !focusedLive) return;

    const monthKey = (() => {
      const d = new Date();
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    })();
    const rosterParent = `${focusedUid}_${monthKey}`;
    const rosterTouched = { current: false };

    const rosterBeat = () => {
      const uid = auth.currentUser?.uid;
      if (uid === focusedUid) return; // self-views never meter
      const rosterRef = uid
        ? doc(db, 'streamAudience', rosterParent, 'viewers', uid)
        : doc(db, 'streamAudience', rosterParent, 'anon', anonSessionId.current);
      const delta = rosterTouched.current ? 20 : 0;
      updateDoc(rosterRef, {
        lastSeenAt: serverTimestamp(),
        watchSeconds: increment(delta),
      }).then(() => { rosterTouched.current = true; }).catch(() => {
        setDoc(rosterRef, {
          firstSeenAt: serverTimestamp(),
          lastSeenAt: serverTimestamp(),
          watchSeconds: 0,
        }).then(() => { rosterTouched.current = true; }).catch(() => {});
      });
    };

    rosterBeat();
    const interval = setInterval(rosterBeat, 20000);
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') rosterBeat();
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isFocused, focusedUid, focusedLive]);

  // Public handle for chat attribution. public_profiles.userName is the
  // canonical public name — the auth/users displayName can be the person's
  // REAL name and never touches chat.
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    getDoc(doc(db, 'public_profiles', uid)).then((snap) => {
      const n = snap.exists() ? snap.data().userName : null;
      if (n) setMyName(n);
    }).catch(() => {});
    const unsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      const d = snap.data() || {};
      setMyName((prev) => prev || d.userName || null);
    }, () => {});
    return () => unsub();
  }, []);

  // ---------------------------------------------------------------------
  // Squad Rumble (evergreen 3.3) - one purchase to the SQUAD. The server
  // snapshots and splits it across the live, linked members who can receive
  // it (admins and unverified members left out), so this page never builds a
  // split: getTeamStream's rumblesEnabled already says whether anyone can
  // take one. Squad members never get the door (you can't Rumble your own
  // squad; the server refuses it too). Stable target + callbacks so
  // StreamRumble's checkout mount survives screen re-renders.
  // ---------------------------------------------------------------------
  const rumbleTarget = useMemo(
    () => (teamStreamId ? { targetType: 'team', targetId: teamStreamId } : null),
    [teamStreamId]
  );
  const canSquadRumble = team?.rumblesEnabled === true && team?.forId === teamStreamId && !isSquadMember;

  const openRumble = () => {
    // A stale tap between polls: the chat hides the button on the same value.
    if (!canSquadRumble) return;
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    // Off web the picker explains itself (web-app only), so no silent return.
    setRumbleOpenFor(teamStreamId);
  };

  const closeRumble = useCallback(() => setRumbleOpenFor(null), []);
  const rumbleRequireAuth = useCallback(() => navigation.navigate('SignUp'), [navigation]);

  // ---------------------------------------------------------------------
  // Team memberships (evergreen 3.5b, 13.8). ONE predicate (showTeamJoin)
  // for the info block's Join and the chat's members-only banner, so they
  // never disagree and never offer a Join the server would refuse.
  // ---------------------------------------------------------------------
  // opener = the element focus returns to when the modal closes (web).
  const startTeamJoin = (opener) => {
    if (!showTeamJoin) return;
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    joinOpenerRef.current = opener || null;
    setJoinOpenFor(teamStreamId);
  };
  // The chat's members-only banner Join, and the info block's button (which
  // gets focus back on close).
  const openTeamJoin = () => startTeamJoin(null);
  const openTeamJoinFromInfo = () => startTeamJoin(teamJoinBtnRef.current);
  const closeTeamJoin = useCallback(() => {
    setJoinOpenFor(null);
    restoreFocus(joinOpenerRef);
    joinOpenerRef.current = null;
  }, []);
  const teamJoinTarget = useMemo(
    () => (teamStreamId ? { kind: 'team', id: teamStreamId, name: joinName } : null),
    [teamStreamId, joinName]
  );
  const teamBadgeTarget = useMemo(
    () => (teamStreamId ? { kind: 'team', id: teamStreamId } : null),
    [teamStreamId]
  );
  // What the chat needs from the viewer's own team pair doc (the hook's ms
  // fields; no raw Timestamp), memoized so StreamChat's effects stay quiet.
  const tmDoc = tm.doc || null;
  const tmInfo = useMemo(() => (tmDoc ? {
    status: tmDoc.status,
    activeUntilMs: Number(tmDoc.activeUntilMs) || 0,
    updatedAtMs: Number(tmDoc.updatedAtMs) || 0,
    removedByCreator: tmDoc.removedByCreator === true,
    badgePerk: tmDoc.perks?.badge === true,
  } : null), [
    tmDoc?.status, tmDoc?.activeUntilMs, tmDoc?.updatedAtMs,
    tmDoc?.removedByCreator, tmDoc?.perks?.badge,
  ]);
  const toggleTeamMembersOnly = useCallback(
    (on) => setTeamChatMembersOnly({ teamId: teamStreamId, on: on === true }),
    [teamStreamId]
  );

  // ---------------------------------------------------------------------
  // Squad Super Crit — one purchase, attributed EQUALLY to every member of
  // the split set (live + linked + verified; the computeSquadSplit mirror
  // runs over the same set). One-tap on a saved card, embedded checkout
  // fallback — exactly the personal-stream flow with the team callables.
  // ---------------------------------------------------------------------
  const openSuperCrit = async () => {
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    if (Platform.OS !== 'web') return;
    if (!linkOn) {
      showError('Squad link is off', 'This squad is not squaded up right now - check back when the crew links up.');
      return;
    }
    if (linkedLiveMembers.length === 0) {
      showError('No linked members live', 'Squad Super Crits support the verified members streaming and linked up. Come back when the crew links up.');
      return;
    }
    // Members are live and linked, but none can receive Super Crits yet (a
    // stale tap between polls; the button is hidden otherwise).
    if (splitMembers.length === 0) {
      showError('Not open yet', SQUAD_TIPS_NOT_OPEN);
      return;
    }
    setSuperError(null);
    setSuperSent(false);
    setSuperEmbedded(null);
    superTips.reset();
    setShowSuper(true);
    try {
      const fn = httpsCallable(functions, 'getTeamSuperCritSetup');
      const result = await fn({ teamStreamId });
      // Tagged with the squad it answers for: tipsEnabled / recipientCount
      // are per-squad, so a setup left over from another squad (deep-link
      // switch) is never read as this one's.
      setSuperSetup(result.data ? { ...result.data, forId: teamStreamId } : null);
      // 'limits' mode: a tier above the room is disabled, so start on the
      // largest one that fits.
      const lim = result.data?.limit;
      const tiers = Array.isArray(result.data?.tiers) ? result.data.tiers : null;
      if (lim && tiers) {
        setSuperTier((cur) => {
          const curTier = tiers.find((t) => t.id === cur);
          if (!curTier || fitsLimit(lim, curTier.cents)) return cur;
          const fit = tiers.filter((t) => fitsLimit(lim, t.cents)).sort((a, b) => b.cents - a.cents)[0];
          return fit ? fit.id : cur;
        });
      }
    } catch (err) {
      console.warn('[TeamStream] super crit setup failed:', err.message);
      setSuperSetup(null);
    }
  };

  // 'limits' mode: a cooldown ended, so re-read the room (no reset).
  const refreshSuperSetup = async () => {
    try {
      const fn = httpsCallable(functions, 'getTeamSuperCritSetup');
      const result = await fn({ teamStreamId });
      setSuperSetup(result.data ? { ...result.data, forId: teamStreamId } : null);
      superTips.setLimitErr(null);
    } catch (err) {
      console.warn('[TeamStream] super crit setup refresh failed:', err.message);
    }
  };

  const closeSuperCrit = useCallback(() => {
    try { superCheckoutInstanceRef.current?.destroy(); } catch (_) { /* noop */ }
    superCheckoutInstanceRef.current = null;
    setSuperEmbedded(null);
    setShowSuper(false);
    setSuperMsg('');
    superTips.reset();
  }, [superTips.reset]); // eslint-disable-line react-hooks/exhaustive-deps

  // The verify button (L11) closes the modal first: a React Native Web modal
  // stays on screen over the next route otherwise.
  const verifyFromSuper = () => {
    closeSuperCrit();
    navigation.navigate('IdVerification');
  };

  // previewV: 2 on both purchase calls = "my preview used the split set"
  // (verified members only). Without it the server refuses with a refresh
  // prompt whenever the verified set differs from live + linked.
  const startSuperEmbedded = async () => {
    const fn = httpsCallable(functions, 'createTeamSuperCritCheckout');
    const result = await fn({
      teamStreamId, tierId: superTier, message: superMsg, sessionId: team?.sessionId, previewV: 2,
    });
    if (!result.data?.clientSecret) throw new Error('Could not start checkout.');
    setSuperEmbedded({
      clientSecret: result.data.clientSecret,
      publishableKey: result.data.publishableKey || superSetup?.publishableKey,
      cardCheck: result.data.cardCheck === true,
      purchaseId: typeof result.data.purchaseId === 'string' ? result.data.purchaseId : null,
    });
  };

  const sendSuperCrit = async () => {
    if (superBusy || superNotOpen || superLimitBlocks) return;
    setSuperBusy(true);
    setSuperError(null);
    superTips.clearNotices();
    try {
      // 'limits' mode: a saved card the setup says can't be used here goes
      // straight to checkout (L5s / L6 shows in the panel).
      if (superSetup?.hasSavedCard && superSavedStatus === 'ok') {
        try {
          // 130s client timeout: the server may run up to its 120s limit, so the
          // SDK's 70s default would give up while the charge could still land.
          const fn = httpsCallable(functions, 'teamSuperCritOneTap', { timeout: 130000 });
          await fn({ teamStreamId, tierId: superTier, message: superMsg, sessionId: team?.sessionId, previewV: 2 });
          setSuperSent(true);
          setTimeout(closeSuperCrit, 1600);
          return;
        } catch (err) {
          const raw = String(err?.message || '');
          // No server answer (client deadline, or the bare 'internal' the SDK
          // reports for a dropped connection): the charge may still land, so
          // treat it as tips-pending rather than showing the raw code or
          // letting a re-tap start a second charge.
          const code = String(err?.code || '');
          if (code === 'functions/deadline-exceeded' || (code === 'functions/internal' && raw === 'internal')) {
            throw new Error("tips-pending: We couldn't confirm your Super Crit yet. Please wait a minute before sending another.");
          }
          // 3.2 refusals never fall through to checkout. tips-unavailable =
          // the squad can't take Super Crits right now; tips-pending = the
          // saved-card charge could not be confirmed and may still land, so
          // a second payment form could charge the fan twice. Both show the
          // server text (after the first colon) with the spinner stopped.
          if (raw.includes('tips-unavailable') || raw.includes('tips-pending')) throw err;
          // 'limits' mode with card checks on: the saved card can't be used
          // here (card-limit / card-blocked; nothing was charged). Fall
          // through to embedded checkout with L5 / L6 shown above it.
          const cardStatus = cardStatusFromError(err);
          if (cardStatus) superTips.setCardRefusal(cardStatus);
          // Saved card declined / needs SCA -> fall through to embedded checkout.
          else if (!raw.includes('authentication-required') && !raw.includes('no-saved-card')) {
            throw err;
          }
        }
      }
      await startSuperEmbedded();
    } catch (err) {
      const raw = String(err?.message || '');
      // 'limits' mode: a tips-limit refusal carries the room (L2-L4).
      const lim = limitFromError(err);
      if (lim) superTips.setLimitErr({ limit: lim, amountCents: selectedTierCents });
      else setSuperError(raw.replace(/^.*?:/, '').trim() || 'Could not send the Super Crit.');
    } finally {
      setSuperBusy(false);
    }
  };

  // Mount the embedded checkout whenever we have a clientSecret + container
  // (same mount/teardown discipline as StreamWatch / TsunamiMeter).
  useEffect(() => {
    if (Platform.OS !== 'web' || !superEmbedded?.clientSecret) return;
    let cancelled = false;

    (async () => {
      try {
        if (typeof superEmbedded.publishableKey !== 'string' || !superEmbedded.publishableKey) {
          throw new Error('Stripe publishable key is not configured.');
        }
        const { loadStripe } = await import('@stripe/stripe-js');
        const stripe = await loadStripe(superEmbedded.publishableKey);
        if (cancelled || !stripe) return;

        const instance = await stripe.createEmbeddedCheckoutPage({
          clientSecret: superEmbedded.clientSecret,
          onComplete: () => {
            // Paid — the webhook drops the highlighted squad message into chat.
            try { superCheckoutInstanceRef.current?.destroy(); } catch (_) { /* noop */ }
            superCheckoutInstanceRef.current = null;
            const held = superEmbedded.cardCheck === true && superEmbedded.purchaseId ? superEmbedded.purchaseId : null;
            setSuperEmbedded(null);
            if (!held) {
              setSuperSent(true);
              setTimeout(closeSuperCrit, 1600);
              return;
            }
            // Card checks on ('limits' mode): only authorized until the
            // server checks the card, so confirm before saying sent (3.7).
            superTips.confirmHeld('team_super_crit', held, {
              onCompleted: () => { setSuperSent(true); setTimeout(closeSuperCrit, 1600); },
              onFailed: () => setSuperError('Could not send the Super Crit.'),
            });
          },
        });

        if (cancelled) { try { instance.destroy(); } catch (_) { /* noop */ } return; }
        superCheckoutInstanceRef.current = instance;
        if (superCheckoutContainerRef.current) {
          instance.mount(superCheckoutContainerRef.current);
        }
      } catch (err) {
        console.error('[TeamStream] embedded checkout mount failed:', err);
        if (!cancelled) {
          setSuperEmbedded(null);
          setSuperError('Could not open the payment form. Please try again.');
        }
      }
    })();

    return () => {
      cancelled = true;
      try { superCheckoutInstanceRef.current?.destroy(); } catch (_) { /* noop */ }
      superCheckoutInstanceRef.current = null;
    };
  }, [superEmbedded, closeSuperCrit]);

  const selectedTierCents = (superSetup?.tiers || FALLBACK_TIERS).find((t) => t.id === superTier)?.cents || 0;
  // 'limits' mode: at the limit, or the picked tier is above the room.
  const superLimitBlocks = !!superLimit
    && (!!limitLines(superLimit)?.atLimit || !fitsLimit(superLimit, selectedTierCents));

  // REQUIRED copy treatment: the split preview is celebratory and money-free
  // (3.2) - it says HOW MANY members share the Super Crit, never a per-member
  // dollar amount (each member is credited their share net of fees, so a
  // gross figure would not be what they receive). No names either: the count
  // alone keeps who is or is not verified off the fan's screen. When the
  // squad can't take one right now, the not-open line replaces it.
  const renderSplitPreview = () => {
    if (superNotOpen) {
      return <Text style={styles.modalHint}>{SQUAD_TIPS_NOT_OPEN}</Text>;
    }
    if (!members.length || !selectedTierCents) return null;
    let splits;
    try {
      // Founder rules (2026-07-21, + verified in 3.2): only members LIVE,
      // LINKED and VERIFIED right now share the split - the same filter as
      // the server's recipient snapshot.
      splits = computeSquadSplit({ amountCents: selectedTierCents, memberUids: splitMembers.map((m) => m.uid) });
    } catch (_) {
      splits = [];
    }
    const count = setupRecipientCount ?? (Array.isArray(splits) ? splits.length : 0);
    if (!count) return null;
    return (
      <View style={styles.splitPreviewBox}>
        <Text style={styles.splitPreviewHeadline}>
          {`Shared by ${count} squad member${count === 1 ? '' : 's'} streaming now`}
        </Text>
        <Text style={styles.splitPreviewSub}>
          One Super Crit, one big moment for the crew.
        </Text>
      </View>
    );
  };

  // ---------------------------------------------------------------------
  // Moderation + reporting (StreamWatch pattern; squad chat moderator = the
  // squad owner, or an admin, server-enforced)
  // ---------------------------------------------------------------------
  const openProfile = (userName) => {
    if (!userName || userName === 'Player' || userName === 'Someone') return;
    navigation.navigate('PublicProfile', { userName });
  };

  const toggleHideUser = (targetUid) => {
    setHiddenUids((prev) => {
      const next = new Set(prev);
      if (next.has(targetUid)) next.delete(targetUid);
      else next.add(targetUid);
      return next;
    });
    setChatUserCard(null);
  };

  const toggleMuteUser = async (targetUid) => {
    if (modBusy || !team?.sessionId) return;
    setModBusy(true);
    try {
      const fnName = mutedUids.has(targetUid) ? 'unmuteStreamChatUser' : 'muteStreamChatUser';
      await httpsCallable(functions, fnName)({ sessionId: team.sessionId, userId: targetUid });
      setMutedUids((prev) => {
        const next = new Set(prev);
        if (next.has(targetUid)) next.delete(targetUid);
        else next.add(targetUid);
        return next;
      });
      setChatUserCard(null);
    } catch (err) {
      console.warn('[TeamStream] mute toggle failed:', err.message);
    } finally {
      setModBusy(false);
    }
  };

  const openReport = (kind, target) => {
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    setReportReason('inappropriate');
    setReportDetails('');
    setReportStatus(null);
    setReportTarget({ kind, ...target });
    setChatUserCard(null);
  };

  const submitReport = async () => {
    if (reportBusy || !reportTarget) return;
    setReportBusy(true);
    setReportStatus(null);
    try {
      await httpsCallable(functions, 'reportStreamContent')({
        kind: reportTarget.kind,
        // Stream reports anchor on the reported member; chat reports anchor
        // on the squad owner (the session's moderator).
        streamerUid: reportTarget.kind === 'stream' ? reportTarget.uid : (team?.ownerUid || reportTarget.uid),
        targetUid: reportTarget.uid,
        sessionId: team?.sessionId || null,
        messageId: reportTarget.messageId || null,
        messageText: reportTarget.messageText || null,
        reason: reportReason,
        details: reportDetails,
      });
      setReportStatus('sent');
      setTimeout(() => setReportTarget(null), 1600);
    } catch (err) {
      const raw = String(err?.message || '');
      setReportStatus(raw.includes('already-reported')
        ? "You've already reported this - our team is on it."
        : 'Could not send the report. Please try again.');
    } finally {
      setReportBusy(false);
    }
  };

  const goBackToStreams = () => {
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('Streams');
  };

  // ---------------------------------------------------------------------
  // Layout math. Wide: an equal 16:9 grid (1 col for one tile, 2 cols for
  // two to four) that fits the viewport height with headroom for the info
  // block and the rail underneath (StreamWatch approach). Narrow: every tile
  // is full width, stacked.
  // ---------------------------------------------------------------------
  const chatW = isWide ? CHAT_WIDTH : 0;
  const contentWidth = isWide ? width - chatW - 48 : width - 32;
  const tileCount = Math.max(1, shownTiles.length);
  const cols = isWide && tileCount >= 2 ? 2 : 1;
  const rows = Math.ceil(tileCount / cols);
  const heightBudget = Math.max(200, height - 320);
  const maxTileByHeight = isWide ? (((heightBudget - MOSAIC_GAP * (rows - 1)) / rows) * 16) / 9 : Infinity;
  // Integer tile width, container derived from it exactly: a fractional tile
  // inside a rounded container wraps the second column onto its own row.
  const tileWidth = isWide
    ? Math.floor(Math.max(240, Math.min((contentWidth - MOSAIC_GAP * (cols - 1)) / cols, maxTileByHeight)))
    : contentWidth;
  const mosaicWidth = isWide ? cols * tileWidth + MOSAIC_GAP * (cols - 1) : contentWidth;
  const atCap = shownTiles.length >= maxTiles;

  // ---------------------------------------------------------------------
  // Renders
  // ---------------------------------------------------------------------
  const renderAvatar = (m, size) => (
    m.avatarUrl ? (
      <Image source={{ uri: m.avatarUrl }} style={{ width: size, height: size, borderRadius: size / 2 }} />
    ) : (
      <View style={[styles.avatarFallback, { width: size, height: size, borderRadius: size / 2 }]}>
        <Text style={styles.avatarFallbackText}>{initials(m.userName)}</Text>
      </View>
    )
  );

  // Poster rail: every member NOT on screen (live ones tap in; offline ones
  // are just posters). Replaces both the old muted mini-player grid and the
  // narrow thumb strip.
  const renderRail = () => {
    const others = members.filter((m) => !shownUids.includes(m.uid));
    if (!others.length && !(isWide && liveCount > 1)) return null;
    const hint = liveCount === 0
      ? null
      : atCap
        ? (maxTiles === 1 ? 'Tap a live member to watch them instead' : 'Tap a live member to swap them in')
        : 'Tap a live member to add them to the screen';
    return (
      <View style={[styles.railBox, { width: isWide ? mosaicWidth : undefined }]}>
        <View style={styles.railHeader}>
          <Text style={styles.railTitle}>Squad</Text>
          {hint ? <Text style={styles.railHint} numberOfLines={1}>{hint}</Text> : null}
          {isWide && liveCount > MOSAIC_DEFAULT_WIDE ? (
            <TouchableOpacity
              style={[styles.capPill, capWide >= MOSAIC_MAX_WIDE && styles.capPillActive]}
              onPress={() => setWideCap(capWide >= MOSAIC_MAX_WIDE ? MOSAIC_DEFAULT_WIDE : MOSAIC_MAX_WIDE)}
            >
              <Ionicons name="grid-outline" size={12} color={capWide >= MOSAIC_MAX_WIDE ? '#0b0b0b' : COLORS.GOLD} style={{ marginRight: 5 }} />
              <Text style={[styles.capPillText, capWide >= MOSAIC_MAX_WIDE && styles.capPillTextActive]}>
                {capWide >= MOSAIC_MAX_WIDE ? 'Back to 2' : 'Show up to 4'}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
        {others.length ? (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.rail}>
            {others.map((m) => {
              const playable = m.live && !!m.playbackUrl;
              return (
                <TouchableOpacity
                  key={`rail-${m.uid}`}
                  style={[styles.posterCard, playable && styles.posterCardLive]}
                  onPress={() => { if (playable) addOrSwapMember(m.uid); }}
                  activeOpacity={playable ? 0.8 : 1}
                  accessibilityLabel={playable ? `Watch ${m.userName}` : `${m.userName} is offline`}
                >
                  {renderAvatar(m, 44)}
                  {playable ? (
                    <View style={styles.posterLiveBadge}><Text style={styles.tileLiveBadgeText}>LIVE</Text></View>
                  ) : null}
                  <Text style={styles.posterName} numberOfLines={1}>{m.userName}</Text>
                  <Text style={styles.posterMeta} numberOfLines={1}>
                    {playable ? `${fmtCount(m.viewers)} watching` : 'Offline'}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        ) : (
          <Text style={styles.railEmpty}>Everyone live is on screen.</Text>
        )}
      </View>
    );
  };

  // The mosaic itself: one MosaicTile per shown member, or the offline box
  // when nobody playable is selected (which, per the reconciliation effect,
  // means nobody is live).
  const renderMosaic = () => (
    shownTiles.length ? (
      <View style={[styles.mosaic, { width: isWide ? mosaicWidth : '100%' }]}>
        {shownTiles.map((m) => (
          <MosaicTile
            key={m.uid}
            member={m}
            width={isWide ? tileWidth : '100%'}
            hasAudio={m.uid === focusedUid}
            audioOn={audioOn}
            canRemove={shownTiles.length > 1}
            onSelectAudio={() => selectAudio(m.uid)}
            onToggleAudio={() => setAudioOn((o) => !o)}
            onRemove={() => removeTile(m.uid)}
            onAutoplayBlocked={() => setAudioOn(false)}
          />
        ))}
      </View>
    ) : (
      <View style={[styles.playerBox, { width: isWide ? mosaicWidth : '100%' }]}>
        <View style={styles.offlineVideo}>
          {focusedMember ? renderAvatar(focusedMember, 56) : null}
          <Text style={styles.offlineTitle}>
            {liveCount > 0
              ? 'Pick a live squad member below'
              : (focusedMember ? `${focusedMember.userName} is offline` : 'The squad is warming up')}
          </Text>
        </View>
      </View>
    )
  );

  const renderInfoBlock = () => (
    <View style={[styles.infoBox, { maxWidth: isWide ? mosaicWidth : undefined }]}>
      <View style={styles.infoTitleRow}>
        <Ionicons name="people" size={18} color={COLORS.GOLD} style={{ marginRight: 8 }} />
        {/* The squad name links to the team page, only while the team
            exists (a squad whose team is gone reads 'Squad'). */}
        {team.ownerUid ? (
          <Pressable
            style={styles.squadNameLink}
            onPress={() => navigation.navigate('TeamPage', { teamId: teamStreamId })}
            accessibilityRole="link"
            accessibilityLabel="Team page"
          >
            <Text style={[styles.squadName, styles.squadNameInLink]} numberOfLines={1}>{team.name || 'Squad stream'}</Text>
          </Pressable>
        ) : (
          <Text style={styles.squadName} numberOfLines={1}>{team.name || 'Squad stream'}</Text>
        )}
        {liveCount > 0 ? (
          <View style={styles.liveChip}><Text style={styles.liveChipText}>LIVE</Text></View>
        ) : null}
      </View>
      <Text style={styles.infoMeta}>
        {members.length} squad member{members.length === 1 ? '' : 's'}
        {liveCount > 0 ? `  |  ${liveCount} live  |  ${fmtCount(totalViewers)} watching` : '  |  offline right now'}
        {linkOn ? '' : '  |  not squaded up right now'}
      </Text>
      {/* Team memberships (3.5b): Join or Member, from the pair listener. */}
      {showTeamMember ? (
        <Pressable
          ref={memberBtnRef}
          style={[styles.teamMembershipBtn, styles.teamMemberBtn]}
          onPress={() => setMemberPanelOpen(true)}
          accessibilityRole="button"
          accessibilityLabel={`Member of ${teamNoun}`}
        >
          <Ionicons name="star" size={13} color={COLORS.GOLD} style={{ marginRight: 6 }} />
          <Text style={styles.teamMembershipBtnText}>Member</Text>
        </Pressable>
      ) : showTeamJoin ? (
        <Pressable
          ref={teamJoinBtnRef}
          style={[styles.teamMembershipBtn, styles.teamJoinBtn]}
          onPress={openTeamJoinFromInfo}
          accessibilityRole="button"
          accessibilityLabel={`Join ${teamNoun}`}
        >
          <Ionicons name="star-outline" size={13} color={COLORS.GOLD} style={{ marginRight: 6 }} />
          <Text style={styles.teamMembershipBtnText}>{`Join ${teamNoun}`}</Text>
        </Pressable>
      ) : null}
      {focusedMember?.live && (focusedMember.title || memberGameLabel(focusedMember)) ? (
        <Text style={styles.infoFocusLine} numberOfLines={1}>
          Watching{' '}
          <Text style={styles.infoFocusName} onPress={() => openProfile(focusedMember.userName)}>
            {focusedMember.userName}
          </Text>
          {focusedMember.title ? ` - ${focusedMember.title}` : ''}
          {memberGameLabel(focusedMember) ? `  (${memberGameLabel(focusedMember)})` : ''}
        </Text>
      ) : null}
      {/* Member-only (3.2, Rumbles 3.3): an unverified member is left out of
          squad Super Crit and Rumble splits, so tell them - only the member
          themselves sees this, and only when the server says so (explicit
          tipEligible false). */}
      {myMember?.tipEligible === false ? (
        <View style={styles.verifyNotice}>
          <Ionicons name="id-card-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 8 }} />
          <Text style={styles.verifyNoticeText}>
            {teamEverLaunched === true
              ? 'Verify to be included when the squad gets Super Crits, Rumbles and team memberships.'
              : 'Verify to be included when the squad gets Super Crits and Rumbles.'}
          </Text>
          <TouchableOpacity style={styles.verifyNoticeBtn} onPress={() => navigation.navigate('IdVerification')}>
            <Text style={styles.verifyNoticeBtnText}>Verify</Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );

  const renderMainColumn = () => {
    return (
      <>
        {renderMosaic()}
        {renderRail()}
        {renderInfoBlock()}
        <TouchableOpacity style={styles.backButton} onPress={goBackToStreams}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Ionicons name="arrow-back" size={18} color="#888" style={{ marginRight: 4 }} />
            <Text style={styles.backButtonText}>Back to Streams</Text>
          </View>
        </TouchableOpacity>
      </>
    );
  };

  const chatSessionId = team && team.status !== 'ended' ? (team.sessionId || null) : null;

  const chatProps = {
    sessionId: chatSessionId,
    senderName: myName,
    streamerUid: team?.ownerUid || null,
    navigation,
    onRumble: openRumble,
    onSuperCrit: openSuperCrit,
    // A live member badge also hands over its tenure (m) and kind (t: team).
    onUserPress: (msg, memberBadge) => setChatUserCard({
      user: msg.user,
      userId: msg.userId,
      messageId: msg.id,
      messageText: msg.message,
      memberMonths: memberBadge ? (Number(memberBadge.m) || 0) : null,
      teamBadge: memberBadge?.t === true,
    }),
    hiddenUids,
    title: 'Squad Chat',
    // Squad members can't Super Crit or Rumble their own squad (both
    // server-enforced), so both lanes hide for them. Super Crit also hides
    // when the squad can't take one at all (link off, or nobody live +
    // linked + verified - the split set is empty); Rumble shows only on the
    // server's rumblesEnabled (3.3).
    hideSuperCrit: isSquadMember || !canSquadSuperCrit,
    rumblesEnabled: canSquadRumble,
    // Team memberships (3.5b, 13.8/13.9): the squad room is the TEAM's room.
    // membershipsLive is the server's raw team gate (undefined = unknown).
    // The roster is admitted by the rule, so it never sees the members-only
    // banner; for everyone else null = unknown (the hook loads or failed).
    // memberInfo follows the pair alone, so a payer who later joined the
    // roster still claims the team badge.
    gateKey: teamStreamId,
    gateKind: 'team',
    membershipsLive: team?.membershipsLive,
    viewerIsMember: onRoster ? true : (tm.loading || tm.error ? null : tm.canChatAsMember),
    memberInfo: tm.isMember ? tmInfo : null,
    onJoin: showTeamJoin ? openTeamJoin : null,
    joinLabel: `Join ${teamNoun}`,
    badgeTarget: teamBadgeTarget,
    canToggleMembersOnly: isOwner && (
      team?.membershipSelf?.membersChatAvailable === true || team?.membershipSelf?.paused === true
    ),
    onToggleMembersOnly: isOwner ? toggleTeamMembersOnly : null,
    newJoinsClosed: team?.membershipSelf?.paused === true || !teamLive,
  };

  // Wide mode hard-locks the page to the viewport (StreamWatch behavior).
  const lockToViewport = isWide && Platform.OS === 'web';

  return (
    <View style={[styles.container, lockToViewport && styles.containerLocked]}>
      <Header navigation={navigation} />

      {/* Squad Rumble (evergreen 3.3): meter, picker modal and burst watcher
          in ONE screen-level mount at a fixed spot in the tree, outside the
          wide / stacked branches and outside StreamChat, so crossing the
          breakpoint or a failed poll (team -> null) never unmounts an open
          payment form. Keyed by squad: a switch starts it fresh. The wrapper
          only gives the meter card the page gutter (the modal and burst
          portal out of it). */}
      {rumbleTarget ? (
        <View style={styles.rumbleMount}>
          <StreamRumble
            key={`team:${teamStreamId}`}
            target={rumbleTarget}
            sessionId={chatSessionId}
            rumblesEnabled={canSquadRumble}
            open={rumbleOpenFor === teamStreamId}
            onClose={closeRumble}
            onRequireAuth={rumbleRequireAuth}
          />
        </View>
      ) : null}

      {/* Join the team (3.5b): screen level beside the Rumble picker, for the
          same reason, and keyed by squad, so a switch tears the payment
          form down. The pair listener, never the poll, says who is a member. */}
      {teamJoinTarget ? (
        <JoinMembershipModal
          key={'join:team:' + teamStreamId}
          target={teamJoinTarget}
          visible={joinOpenFor === teamStreamId}
          onClose={closeTeamJoin}
          navigation={navigation}
        />
      ) : null}

      {loading ? (
        <ActivityIndicator color={COLORS.GOLD} style={{ marginVertical: 60 }} />
      ) : !team ? (
        <View style={styles.notFoundBox}>
          <Ionicons name="people-outline" size={34} color={COLORS.DIM} />
          <Text style={styles.offlineTitle}>Squad not found</Text>
          <TouchableOpacity style={styles.browseBtn} onPress={goBackToStreams}>
            <Text style={styles.browseBtnText}>Browse live streams</Text>
          </TouchableOpacity>
        </View>
      ) : team.status === 'ended' ? (
        <View style={styles.notFoundBox}>
          <Ionicons name="flag-outline" size={34} color={COLORS.DIM} />
          <Text style={styles.offlineTitle}>This squad has wrapped up</Text>
          {/* The team page is the evergreen one (3.5b): a fan who arrives
              after the squad ended can still reach the team, but never a
              "Visit Team Squad" over a team that is gone. */}
          <View style={styles.endedBtnRow}>
            <TouchableOpacity style={styles.browseBtn} onPress={goBackToStreams}>
              <Text style={styles.browseBtnText}>Browse live streams</Text>
            </TouchableOpacity>
            {team.ownerUid ? (
              <TouchableOpacity
                style={styles.visitTeamBtn}
                onPress={() => navigation.navigate('TeamPage', { teamId: teamStreamId })}
                accessibilityRole="button"
                accessibilityLabel={`Visit ${teamDisplayName(team.name)}`}
              >
                <Text style={styles.visitTeamBtnText}>{`Visit ${teamDisplayName(team.name)}`}</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
      ) : isWide ? (
        <View style={styles.wideBody}>
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={[styles.content, styles.contentCentered]}
            showsVerticalScrollIndicator={false}
          >
            <View style={{ width: mosaicWidth, maxWidth: '100%' }}>
              {renderMainColumn()}
            </View>
          </ScrollView>
          <StreamChat {...chatProps} style={styles.chatRail} />
        </View>
      ) : (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          {renderMainColumn()}
          <StreamChat {...chatProps} style={styles.chatStacked} />
        </ScrollView>
      )}

      {/* Squad Super Crit modal — one highlighted message, shared by the crew */}
      <Modal visible={showSuper} transparent animationType="fade" onRequestClose={closeSuperCrit}>
        <Pressable style={styles.modalBackdrop} onPress={closeSuperCrit}>
          <Pressable style={[styles.modalCard, superEmbedded && styles.superCardWide]} onPress={(e) => e.stopPropagation()}>
            {superSent ? (
              <View style={{ alignItems: 'center', paddingVertical: 18 }}>
                <Ionicons name="star" size={34} color={COLORS.GOLD} />
                <Text style={styles.modalSentText}>Super Crit sent - the squad shares it!</Text>
              </View>
            ) : superTips.confirm ? (
              <>
                {/* 'limits' mode: a held checkout payment being confirmed (L9) */}
                <Text style={styles.modalTitle}>Complete your Super Crit</Text>
                {superTips.confirm === 'confirming' ? (
                  <ActivityIndicator color={COLORS.GOLD} style={{ marginTop: 18 }} />
                ) : null}
                <Text style={styles.modalHint}>
                  {superTips.confirm === 'confirming' ? CONFIRMING_TEXT : STILL_CONFIRMING_TEXT}
                </Text>
                {superTips.confirm === 'pending' ? (
                  <TouchableOpacity style={styles.superCancelBtn} onPress={closeSuperCrit}>
                    <Text style={styles.superCancelBtnText}>Close</Text>
                  </TouchableOpacity>
                ) : null}
              </>
            ) : superEmbedded ? (
              <>
                <Text style={styles.modalTitle}>Complete your Super Crit</Text>
                {superTips.cardRefusal ? (
                  <SpendingLimitNotice
                    line={cardLine(superTips.cardRefusal, { verify: superLimit?.verify })}
                    tone="error"
                    onVerify={verifyFromSuper}
                  />
                ) : null}
                <View ref={superCheckoutContainerRef} style={styles.superCheckoutBox} />
                <TouchableOpacity style={styles.superCancelBtn} onPress={closeSuperCrit}>
                  <Text style={styles.superCancelBtnText}>Cancel</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                <Text style={styles.modalTitle}>Super Crit the whole squad</Text>
                <Text style={styles.modalSub}>
                  {team?.name
                    ? `One highlighted message in ${team.name}'s squad chat.`
                    : 'One highlighted message in the squad chat.'}
                </Text>

                {/* 'limits' mode: the room above the tiers (L1, or L3-L4 at
                    the limit); a refusal's own numbers replace it. */}
                {superLimit && !superTips.limitErr ? (
                  <SpendingLimitNotice limit={superLimit} onExpire={refreshSuperSetup} onVerify={verifyFromSuper} />
                ) : null}
                {superTips.limitErr ? (
                  <SpendingLimitNotice
                    limit={superTips.limitErr.limit}
                    amountCents={superTips.limitErr.amountCents}
                    onExpire={refreshSuperSetup}
                    onVerify={verifyFromSuper}
                  />
                ) : null}

                <View style={styles.superTierGrid}>
                  {(superSetup?.tiers || FALLBACK_TIERS).map((t) => (
                    <TouchableOpacity
                      key={t.id}
                      style={[
                        styles.superTierChip,
                        { borderColor: t.color },
                        superTier === t.id && { backgroundColor: t.color },
                        // 'limits' mode: a tier above the room is disabled.
                        !fitsLimit(superLimit, t.cents) && { opacity: 0.35 },
                      ]}
                      onPress={() => { setSuperTier(t.id); superTips.clearNotices(); }}
                      disabled={!fitsLimit(superLimit, t.cents)}
                    >
                      <Text style={[
                        styles.superTierChipText,
                        superTier === t.id && { color: superCritTextColor(t.color) },
                      ]}>
                        {fmtDollars(t.cents)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                {renderSplitPreview()}

                <TextInput
                  style={styles.superMsgInput}
                  placeholder="Say something to the squad (optional)..."
                  placeholderTextColor="#666"
                  value={superMsg}
                  onChangeText={setSuperMsg}
                  maxLength={superSetup?.maxMessageLength || 200}
                  multiline
                />

                {superSetup?.hasSavedCard && superSetup?.card && superSavedStatus === 'ok' ? (
                  <Text style={styles.modalHint}>
                    One tap on your saved {superSetup.card.brand?.toUpperCase()} ending {superSetup.card.last4}
                  </Text>
                ) : null}
                {/* 'limits' mode: the saved card can't be used here (L5s /
                    L6); "Use another card" and Send both open checkout for
                    another card (sendSuperCrit skips one-tap then). */}
                {superSetup?.hasSavedCard && superSavedStatus !== 'ok' ? (
                  <SpendingLimitNotice
                    line={cardLine(superSavedStatus, { saved: true, verify: superLimit?.verify })}
                    tone="error"
                    onVerify={verifyFromSuper}
                    onUseAnotherCard={superLimitBlocks || superNotOpen ? undefined : sendSuperCrit}
                    disabled={superBusy}
                  />
                ) : null}
                {superTips.outcomeLine ? (
                  <SpendingLimitNotice line={superTips.outcomeLine} tone="error" onVerify={verifyFromSuper} />
                ) : null}

                {superError ? <Text style={styles.modalErrorText}>{superError}</Text> : null}

                <TouchableOpacity
                  style={[styles.modalSendBtn, (superBusy || superNotOpen || superLimitBlocks) && { opacity: 0.6 }]}
                  onPress={sendSuperCrit}
                  disabled={superBusy || superNotOpen || superLimitBlocks}
                >
                  {superBusy ? (
                    <ActivityIndicator size="small" color="#0b0b0b" />
                  ) : (
                    <Text style={styles.modalSendBtnText}>
                      Send {fmtDollars(selectedTierCents)} Super Crit
                    </Text>
                  )}
                </TouchableOpacity>
              </>
            )}
          </Pressable>
        </Pressable>
      </Modal>

      {/* Chat user card — profile / hide / mute (owner) / report */}
      <Modal visible={!!chatUserCard} transparent animationType="fade" onRequestClose={() => setChatUserCard(null)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setChatUserCard(null)}>
          <Pressable style={styles.userCard} onPress={(e) => e.stopPropagation()}>
            {chatUserCard ? (
              <>
                <View style={styles.userCardHeader}>
                  <Text style={styles.userCardName}>{chatUserCard.user}</Text>
                </View>
                {/* A live TEAM badge adds its tenure (3.5b; the watch page's
                    tenure row). */}
                {chatUserCard.memberMonths != null && chatUserCard.teamBadge === true ? (
                  <View style={styles.userCardMemberRow}>
                    <Ionicons name="star" size={13} color={COLORS.GOLD} style={{ marginRight: 8 }} />
                    <Text style={styles.userCardMemberText}>{teamMemberTenureLabel(chatUserCard.memberMonths)}</Text>
                  </View>
                ) : null}

                <TouchableOpacity style={styles.userCardAction} onPress={() => { setChatUserCard(null); openProfile(chatUserCard.user); }}>
                  <Ionicons name="person-outline" size={16} color="#ccc" style={{ marginRight: 10 }} />
                  <Text style={styles.userCardActionText}>View profile</Text>
                </TouchableOpacity>

                {chatUserCard.userId !== myUid ? (
                  <>
                    <TouchableOpacity style={styles.userCardAction} onPress={() => toggleHideUser(chatUserCard.userId)}>
                      <Ionicons name={hiddenUids.has(chatUserCard.userId) ? 'eye-outline' : 'eye-off-outline'} size={16} color="#ccc" style={{ marginRight: 10 }} />
                      <Text style={styles.userCardActionText}>
                        {hiddenUids.has(chatUserCard.userId) ? 'Show their messages' : 'Hide their messages (just for you)'}
                      </Text>
                    </TouchableOpacity>

                    {isOwner && chatUserCard.userId !== team?.ownerUid ? (
                      <TouchableOpacity
                        style={styles.userCardAction}
                        onPress={() => toggleMuteUser(chatUserCard.userId)}
                        disabled={modBusy}
                      >
                        <Ionicons name="mic-off-outline" size={16} color={COLORS.GOLD} style={{ marginRight: 10 }} />
                        <Text style={[styles.userCardActionText, { color: COLORS.GOLD }]}>
                          {modBusy ? 'Working...' : mutedUids.has(chatUserCard.userId) ? 'Unmute in this chat' : 'Mute in this chat'}
                        </Text>
                      </TouchableOpacity>
                    ) : null}

                    <TouchableOpacity
                      style={styles.userCardAction}
                      onPress={() => openReport('chat_user', {
                        uid: chatUserCard.userId, name: chatUserCard.user,
                        messageId: chatUserCard.messageId, messageText: chatUserCard.messageText,
                      })}
                    >
                      <Ionicons name="flag-outline" size={16} color="#e05555" style={{ marginRight: 10 }} />
                      <Text style={[styles.userCardActionText, { color: '#e05555' }]}>Report</Text>
                    </TouchableOpacity>
                  </>
                ) : null}
              </>
            ) : null}
          </Pressable>
        </Pressable>
      </Modal>

      {/* Report modal — same pipe as personal streams */}
      <Modal visible={!!reportTarget} transparent animationType="fade" onRequestClose={() => setReportTarget(null)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setReportTarget(null)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            {reportStatus === 'sent' ? (
              <View style={{ alignItems: 'center', paddingVertical: 18 }}>
                <Ionicons name="shield-checkmark" size={34} color={COLORS.GOLD} />
                <Text style={styles.modalSentText}>Report sent - our team will review.</Text>
              </View>
            ) : reportTarget ? (
              <>
                <Text style={styles.modalTitle}>
                  Report {reportTarget.kind === 'stream' ? 'this stream' : reportTarget.name}
                </Text>
                <Text style={styles.modalSub}>
                  Tell us what's wrong - reports go straight to the CritCash team.
                </Text>

                <View style={styles.reportChips}>
                  {[
                    ['inappropriate', 'Inappropriate'],
                    ['harassment', 'Harassment'],
                    ['cheating', 'Cheating'],
                    ['spam', 'Spam'],
                    ['other', 'Other'],
                  ].map(([key, label]) => (
                    <TouchableOpacity
                      key={key}
                      style={[styles.reportChip, reportReason === key && styles.reportChipActive]}
                      onPress={() => setReportReason(key)}
                    >
                      <Text style={[styles.reportChipText, reportReason === key && styles.reportChipTextActive]}>
                        {label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                <TextInput
                  style={styles.superMsgInput}
                  placeholder="Anything else we should know? (optional)"
                  placeholderTextColor="#666"
                  value={reportDetails}
                  onChangeText={setReportDetails}
                  maxLength={500}
                  multiline
                />

                {reportStatus ? <Text style={styles.modalErrorText}>{reportStatus}</Text> : null}

                <TouchableOpacity
                  style={[styles.modalSendBtn, reportBusy && { opacity: 0.6 }]}
                  onPress={submitReport}
                  disabled={reportBusy}
                >
                  {reportBusy ? (
                    <ActivityIndicator size="small" color="#0b0b0b" />
                  ) : (
                    <Text style={styles.modalSendBtnText}>Send report</Text>
                  )}
                </TouchableOpacity>
              </>
            ) : null}
          </Pressable>
        </Pressable>
      </Modal>

      {/* Member panel (3.5b): the shared team panel, from the viewer's own
          pair doc. Money-free; billing lives on Your memberships. */}
      <MemberPanel
        visible={memberPanelOpen && showTeamMember}
        onClose={() => setMemberPanelOpen(false)}
        kind="team"
        name={joinName}
        doc={tm.doc}
        navigation={navigation}
        returnFocusRef={memberBtnRef}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.BG },
  containerLocked: { height: '100vh', maxHeight: '100vh', overflow: 'hidden' },
  // Screen-level StreamRumble (3.3): page gutter for its meter card
  rumbleMount: { paddingHorizontal: 16 },
  content: { padding: 16, paddingBottom: 100 },
  contentCentered: { alignItems: 'center' },
  wideBody: { flex: 1, flexDirection: 'row', minHeight: 0 },

  playerBox: {
    aspectRatio: 16 / 9, borderRadius: 10, overflow: 'hidden', backgroundColor: '#000',
  },
  offlineVideo: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#141414' },
  offlineTitle: { color: COLORS.TEXT, fontSize: 16, fontWeight: '700', marginTop: 10 },
  notFoundBox: { alignItems: 'center', paddingVertical: 60 },
  browseBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 10,
    paddingHorizontal: 18, marginTop: 14,
  },
  browseBtnText: { color: '#0b0b0b', fontSize: 13, fontWeight: '800' },
  // Ended squad: "Browse live streams" beside "Visit Team X" (3.5b)
  endedBtnRow: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 10 },
  visitTeamBtn: {
    borderWidth: 1, borderColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 10,
    paddingHorizontal: 18, marginTop: 14, minHeight: 36, justifyContent: 'center',
  },
  visitTeamBtnText: { color: COLORS.GOLD, fontSize: 13, fontWeight: '800' },

  focusedNameBar: {
    position: 'absolute', top: 8, left: 8,
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: 6,
    paddingVertical: 4, paddingHorizontal: 8, maxWidth: '70%',
  },
  focusedNameText: { color: '#fff', fontSize: 12, fontWeight: '800' },

  // Mosaic (live tiles the viewer picked)
  mosaic: {
    flexDirection: 'row', flexWrap: 'wrap', gap: MOSAIC_GAP,
  },
  mosaicTile: {
    aspectRatio: 16 / 9, borderRadius: 10, overflow: 'hidden', backgroundColor: '#000',
    borderWidth: 2, borderColor: '#1a1a1a',
  },
  mosaicTileAudio: { borderColor: COLORS.GOLD },
  mosaicRemoveBtn: {
    position: 'absolute', top: 8, right: 8, width: 26, height: 26, borderRadius: 13,
    backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center',
  },
  tileLiveBadgeInline: {
    backgroundColor: COLORS.LIVE, borderRadius: 4,
    paddingVertical: 1, paddingHorizontal: 6, marginRight: 6,
  },
  tileLiveBadgeText: { color: '#fff', fontSize: 9, fontWeight: '800', letterSpacing: 0.5 },
  tileViewersText: { color: '#bbb', fontSize: 10 },

  // Poster rail (members not on screen)
  railBox: { marginTop: 12 },
  railHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  railTitle: { color: COLORS.TEXT, fontSize: 13, fontWeight: '800', marginRight: 10 },
  railHint: { color: COLORS.DIM, fontSize: 11.5, flexShrink: 1, flex: 1 },
  railEmpty: { color: COLORS.DIM, fontSize: 12 },
  rail: { flexGrow: 0 },
  capPill: {
    flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: COLORS.GOLD,
    borderRadius: 999, paddingVertical: 4, paddingHorizontal: 10, marginLeft: 10,
  },
  capPillActive: { backgroundColor: COLORS.GOLD },
  capPillText: { color: COLORS.GOLD, fontSize: 11, fontWeight: '800' },
  capPillTextActive: { color: '#0b0b0b' },
  posterCard: {
    width: 120, alignItems: 'center',
    backgroundColor: '#141414', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 10, paddingVertical: 10, paddingHorizontal: 8, marginRight: 8,
  },
  posterCardLive: { borderColor: '#3a3a3a', backgroundColor: '#181818' },
  posterLiveBadge: {
    backgroundColor: COLORS.LIVE, borderRadius: 4, paddingVertical: 1, paddingHorizontal: 6, marginTop: 6,
  },
  posterName: { color: '#eee', fontSize: 11.5, fontWeight: '700', marginTop: 6, maxWidth: '100%' },
  posterMeta: { color: '#888', fontSize: 10, marginTop: 2 },

  // Info block under the player
  infoBox: {
    backgroundColor: '#181818', borderRadius: 12, padding: 14, marginTop: 14,
  },
  infoTitleRow: { flexDirection: 'row', alignItems: 'center' },
  squadName: { color: '#fff', fontSize: 17, fontWeight: '800', flexShrink: 1, marginRight: 8 },
  // The squad name as the team page link (3.5b)
  squadNameLink: { flexShrink: 1, marginRight: 8, minHeight: 36, justifyContent: 'center' },
  squadNameInLink: { marginRight: 0 },
  // Team Join / Member (3.5b; PublicProfileScreen btnJoin / btnMember gold)
  teamMembershipBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', alignSelf: 'flex-start',
    minHeight: 36, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 9, marginTop: 10,
  },
  teamJoinBtn: { backgroundColor: 'rgba(252,208,0,0.10)', borderWidth: 1, borderColor: COLORS.GOLD },
  teamMemberBtn: { backgroundColor: '#161616', borderWidth: 1, borderColor: 'rgba(252,208,0,0.5)' },
  teamMembershipBtnText: { color: COLORS.GOLD, fontSize: 12.5, fontWeight: '800' },
  liveChip: { backgroundColor: COLORS.LIVE, borderRadius: 4, paddingVertical: 2, paddingHorizontal: 7 },
  liveChipText: { color: '#fff', fontSize: 10, fontWeight: '800', letterSpacing: 0.5 },
  infoMeta: { color: COLORS.DIM, fontSize: 12.5, marginTop: 6 },
  infoFocusLine: { color: '#ccc', fontSize: 12.5, marginTop: 8 },
  infoFocusName: { color: COLORS.GOLD, fontWeight: '700' },
  // Member-only verify notice (CreatorMetrics memberBanner idiom)
  verifyNotice: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#1a1604',
    borderColor: '#4a3d00', borderWidth: 1, borderRadius: 8,
    paddingVertical: 10, paddingHorizontal: 12, marginTop: 12,
  },
  verifyNoticeText: { color: '#ccc', fontSize: 12, flex: 1, lineHeight: 17 },
  verifyNoticeBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 6, paddingVertical: 6,
    paddingHorizontal: 12, marginLeft: 10,
  },
  verifyNoticeBtnText: { color: '#0b0b0b', fontSize: 12, fontWeight: '800' },

  backButton: { marginTop: 22, alignSelf: 'flex-start' },
  backButtonText: { color: '#888', fontSize: 14 },

  // Chat placement
  chatRail: { width: CHAT_WIDTH, borderRadius: 0, borderTopWidth: 0, borderRightWidth: 0, borderBottomWidth: 0 },
  chatStacked: { height: 380, marginTop: 16 },

  avatarFallback: { backgroundColor: '#2a3550', alignItems: 'center', justifyContent: 'center' },
  avatarFallbackText: { color: '#b5d4f4', fontSize: 12, fontWeight: '700' },

  // Modals (super crit / report share the card idiom)
  modalBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  modalCard: {
    width: '100%', maxWidth: 380, backgroundColor: COLORS.CARD,
    borderColor: COLORS.CARD_BORDER, borderWidth: 1, borderRadius: 14, padding: 20,
  },
  modalTitle: { color: '#fff', fontSize: 17, fontWeight: '800' },
  modalSub: { color: '#999', fontSize: 12.5, lineHeight: 18, marginTop: 6 },
  modalHint: { color: '#777', fontSize: 12, marginTop: 10, textAlign: 'center' },
  modalErrorText: { color: '#e05555', fontSize: 12, marginTop: 10, textAlign: 'center' },
  modalSendBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 10, paddingVertical: 12,
    alignItems: 'center', marginTop: 14,
  },
  modalSendBtnText: { color: '#0b0b0b', fontSize: 14, fontWeight: '800' },
  modalSentText: { color: COLORS.GOLD, fontSize: 16, fontWeight: '800', marginTop: 10, textAlign: 'center' },

  // Super Crit modal
  superTierGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 16 },
  superTierChip: {
    width: '22%', flexGrow: 1, alignItems: 'center',
    backgroundColor: '#161616', borderWidth: 1.5, borderRadius: 8, paddingVertical: 10,
  },
  superTierChipText: { color: '#ddd', fontSize: 13, fontWeight: '800' },
  superMsgInput: {
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 8, color: COLORS.TEXT, fontSize: 13,
    paddingVertical: 8, paddingHorizontal: 12, marginTop: 12,
    minHeight: 60, textAlignVertical: 'top',
  },
  superCardWide: { maxWidth: 460 },
  superCheckoutBox: { minHeight: 420, marginTop: 14, borderRadius: 10, overflow: 'hidden', backgroundColor: '#fff' },
  superCancelBtn: { alignItems: 'center', marginTop: 10, paddingVertical: 8 },
  superCancelBtnText: { color: '#888', fontSize: 13, fontWeight: '600' },

  // Split preview (REQUIRED copy treatment)
  splitPreviewBox: {
    backgroundColor: '#161310', borderColor: '#3a3010', borderWidth: 1,
    borderRadius: 8, padding: 10, marginTop: 12,
  },
  splitPreviewHeadline: { color: COLORS.GOLD, fontSize: 12.5, fontWeight: '800' },
  splitPreviewSub: { color: '#998a55', fontSize: 11, marginTop: 8, lineHeight: 15 },

  // Chat user card
  userCard: {
    width: '100%', maxWidth: 320, backgroundColor: COLORS.CARD,
    borderColor: COLORS.CARD_BORDER, borderWidth: 1, borderRadius: 14, padding: 8,
  },
  userCardHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingVertical: 10, paddingHorizontal: 12,
    borderBottomWidth: 1, borderBottomColor: COLORS.CARD_BORDER, marginBottom: 4,
  },
  userCardName: { color: '#fff', fontSize: 15, fontWeight: '800' },
  // Team badge tenure (3.5b; StreamWatchScreen userCardMemberRow)
  userCardMemberRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 12 },
  userCardMemberText: { color: COLORS.GOLD, fontSize: 12.5, fontWeight: '700' },
  userCardAction: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 11, paddingHorizontal: 12, borderRadius: 8,
  },
  userCardActionText: { color: '#ccc', fontSize: 13.5, fontWeight: '600' },

  // Report modal reason chips
  reportChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 16 },
  reportChip: {
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 8, paddingVertical: 7, paddingHorizontal: 12,
  },
  reportChipActive: { backgroundColor: '#2a0d0d', borderColor: '#e05555' },
  reportChipText: { color: '#bbb', fontSize: 12, fontWeight: '700' },
  reportChipTextActive: { color: '#ff8a8a' },
});
